// Hook — subscribe to admin broadcasts on a given domain's WebSocket
// and return the latest `playbook_model_changed` event.
//
// Used by the Callback Playbook modal so it can hot-refresh when a
// manager flips the Green Brain toggle in Admin. Reuses the same
// /api/ws/ringing endpoint the ringing popup uses — the hub already
// fans out any payload to every socket subscribed to that domain, so
// we just filter by `type` on the client.
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const wsBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/^http/, 'ws');

export const usePlaybookSocket = (domainId) => {
  const { user, token } = useAuth();
  const [event, setEvent] = useState(null);
  const wsRef = useRef(null);
  const retryRef = useRef(null);

  useEffect(() => {
    if (!domainId || !token) return undefined;
    // Same domain-scope guard as the ringing hook — the WS endpoint
    // enforces this too, but we skip the round-trip when we already
    // know we'd be rejected.
    if (user?.role !== 'master_admin' && user?.domain_id !== domainId) return undefined;
    let cancelled = false;

    const connect = () => {
      const url = `${wsBase()}/api/ws/ringing?domain_id=${encodeURIComponent(domainId)}&token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data?.type === 'playbook_model_changed') {
            setEvent({ ...data, _id: Date.now() });
          }
        } catch (err) {
          console.warn('[usePlaybookSocket] bad message', err);
        }
      };
      ws.onclose = () => {
        if (cancelled) return;
        const delay = 3000 + Math.random() * 2000;
        retryRef.current = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        try { ws.close(); } catch { /* noop */ }
      };
    };

    connect();
    return () => {
      cancelled = true;
      if (retryRef.current) clearTimeout(retryRef.current);
      try { wsRef.current?.close(); } catch { /* noop */ }
    };
  }, [domainId, token, user?.role, user?.domain_id]);

  return event;
};
